import React from 'react';
import Layout from '../components/Layout/Layout';
import { getActiveSeason, CONTACT_EMAIL } from '../config/seasons';
import { REFUND_POLICY } from '../shared/refundPolicy';
import { SeasonAtAGlance, JumpLinks, InlineLink, formatDay, eventWhen, venueText } from '../components/Season/SeasonFacts';

const Email = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>{CONTACT_EMAIL}</a>
);

// Each question opens on tap, so the page reads as a list of questions first.
const Question = ({ q, children }) => (
  <details style={{ background: 'white', borderRadius: '8px', marginBottom: '0.6rem', border: '1px solid #eee' }}>
    <summary style={{ cursor: 'pointer', padding: '0.9rem 1.1rem', fontWeight: 600, color: 'var(--charcoal)', fontSize: '1.02rem' }}>
      {q}
    </summary>
    <div style={{ padding: '0 1.1rem 1rem', color: '#555', lineHeight: 1.65 }}>{children}</div>
  </details>
);

const Group = ({ id, title, children }) => (
  <section id={id} style={{ marginBottom: '2.5rem', scrollMarginTop: '90px' }}>
    <h2 style={{ textAlign: 'left', marginBottom: '1rem' }}>{title}</h2>
    {children}
  </section>
);

const SECTIONS = [
  ['registration', 'Registration & fees'],
  ['ensembles', 'Ensembles'],
  ['schools', 'Schools & teachers'],
  ['solos', 'Solos & music'],
  ['day', 'Competition day'],
  ['photos', 'Photos & video'],
];

const FAQ = () => {
  const season = getActiveSeason();
  const { year } = season;
  const p = season.prices;

  return (
    <Layout
      title="FAQ | Prix du Canada Ballet Competition"
      description={`Answers to common questions about Prix du Canada ${year}: registration, fees, ensembles, schools, music, competition day, photos and video.`}
      keywords="ballet competition FAQ, prix du canada questions, ballet competition fees, ballet ensemble competition, ballet competition Toronto"
    >
      <section className="elegant-hero">
        <div className="container">
          <h1>Frequently Asked Questions</h1>
          <p style={{ textAlign: 'center' }}>Tap a question to see the answer.</p>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--soft-cream)', paddingTop: '2.5rem' }}>
        <div className="container" style={{ maxWidth: '860px', textAlign: 'left' }}>
          <SeasonAtAGlance season={season} />
          <JumpLinks items={SECTIONS} />

          <Group id="registration" title="Registration & fees">
            <Question q="When does registration open and close?">
              <p>
                {new Date() >= new Date(season.opensAt)
                  ? <>Registration is <strong>open now</strong>.</>
                  : <>Registration opens on <strong>{formatDay(season.opensAt)}</strong>.</>}{' '}
                {season.closesAt
                  ? <>It closes on <strong>{formatDay(season.closesAt)}</strong>.</>
                  : 'The closing date will be posted soon. It is usually about 2 weeks before competition day.'}
              </p>
            </Question>

            <Question q="How much does it cost?">
              {p ? (
                <>
                  <p>You pay a registration fee once, plus a fee for each entry:</p>
                  <ul>
                    <li>Registration fee: <strong>${p.registration}</strong> (<strong>${p.registrationEarlyBird}</strong> if you pay by {formatDay(season.earlyBirdEndsAt)})</li>
                    <li>Each solo: <strong>${p.solo}</strong></li>
                    <li>Each ensemble: <strong>${p.ensemble}</strong> per dancer</li>
                  </ul>
                  <p>13% HST is added at checkout. See all fees on the <InlineLink to="/competition-rules#fees">Rules &amp; Fees</InlineLink> page.</p>
                </>
              ) : (
                <p>Fees will be posted when registration opens.</p>
              )}
            </Question>

            {p && season.comboEndsAt && (
              <Question q="What is the solo + ensemble deal?">
                <p>
                  If you enter a solo and an ensemble, you pay <strong>${p.combo}</strong> for the two instead
                  of ${p.solo + p.ensemble}. This deal ends on {formatDay(season.comboEndsAt)}. You get it
                  automatically at checkout.
                </p>
              </Question>
            )}

            <Question q="Can I add a solo or ensemble after I pay?">
              <p>
                Yes. Log in, open your registration and add it any time before registration closes. You only
                pay for what's new.
              </p>
            </Question>

            <Question q="What is your refund policy?">
              <ul>
                {REFUND_POLICY.map((r) => <li key={r.item}><strong>{r.item}:</strong> {r.policy}</li>)}
              </ul>
              <p>To ask for a refund, email <Email />.</p>
            </Question>

            <Question q="Will I get a confirmation email?">
              <p>Yes, right after you pay. If you can't find it, check your spam folder or email <Email />.</p>
            </Question>

            <Question q="Can dancers from outside Canada enter?">
              <p>Yes! Dancers from any country are welcome.</p>
            </Question>
          </Group>

          <Group id="ensembles" title="Ensembles">
            <Question q="What is an ensemble?">
              <p>
                A group of <strong>3 to 8 dancers</strong> who perform one classical ballet piece together. Groups
                of 3–5 can dance up to 3 minutes, and groups of 6–8 up to 4 minutes.
              </p>
            </Question>

            <Question q="How do we register as a team?">
              <ol>
                <li>Each dancer registers on their own account and adds an ensemble.</li>
                <li>One dancer chooses <strong>Start a team</strong> and gets a team code (like <code>PDC-7KQ3</code>).</li>
                <li>They share the code with the group. Everyone else chooses <strong>Join with a code</strong>.</li>
              </ol>
            </Question>

            <Question q="My team isn't ready yet. Can I still sign up?">
              <p>
                Yes. Choose <strong>Join later</strong>. You can pay now (and still get the solo + ensemble deal),
                then join your team any time before registration closes.
              </p>
            </Question>

            <Question q="Who pays for the ensemble?">
              <p>Each dancer pays for their own spot (${p ? p.ensemble : 80} per dancer). Nobody has to collect money for the group.</p>
            </Question>

            <Question q="Can dancers from different schools be in one ensemble?">
              <p>Yes.</p>
            </Question>

            <Question q="Can kids and adults dance in the same ensemble?">
              <p>
                No. Youth ensembles are for dancers 17 and under. Adult ensembles are for dancers 18 and over.
                See the <InlineLink to="/competition-rules#ensembles">ensemble rules</InlineLink> for the age groups.
              </p>
            </Question>
          </Group>

          <Group id="schools" title="Schools & teachers">
            <Question q="I'm a teacher. How do I register my students?">
              <p>
                Go to <InlineLink to="/register">Register</InlineLink> and choose the school option. In the studio
                portal you add your students, their solos and your ensembles in one place. You don't pay online;
                we send you an invoice.
              </p>
            </Question>

            {season.schoolPricing && (
              <Question q="Do schools get a discount?">
                <p>Yes. Schools pay the regular prices, with a discount on the whole order:</p>
                <ul>
                  {season.schoolPricing.tiers.map((t) => (
                    <li key={t.name}>${t.from.toLocaleString('en-CA')} or more: <strong>{t.percentOff}% off</strong></li>
                  ))}
                </ul>
              </Question>
            )}

            <Question q="Do parents need to sign anything?">
              <p>
                Yes, for students under 18. The studio portal emails their parent or guardian a consent form to
                sign online.
              </p>
            </Question>
          </Group>

          <Group id="solos" title="Solos & music">
            <Question q="How many solos can I enter?">
              <p>Up to <strong>2 solos</strong>, and up to 2 ensembles too.</p>
            </Question>

            <Question q="How long can my solo be?">
              <p>Up to <strong>3 minutes</strong>. You can't buy extra time for a solo.</p>
            </Question>

            <Question q="Can I dance a variation that isn't on your list?">
              <p>
                Yes. Save it (with the music) in your registration, then email <Email /> so our judges can check
                it. It must be classical ballet and 3 minutes or less.
              </p>
            </Question>

            <Question q="Do I dance on pointe or in soft shoes?">
              <ul>
                <li><strong>10 and under:</strong> soft shoes only. Pointe is not allowed.</li>
                <li><strong>11:</strong> soft shoes recommended. Pointe is strongly discouraged.</li>
                <li><strong>12 to 17:</strong> pointe is expected. Soft shoes lose technique points.</li>
                <li><strong>Adults:</strong> your choice. Adult Recreational has a separate pointe division and soft shoe division.</li>
              </ul>
              <p>You choose your shoes for each entry when you register. See the <InlineLink to="/competition-rules#shoes">pointe rules</InlineLink>.</p>
            </Question>

            <Question q="Is there a recreational division for kids?">
              <p>No. Recreational is for adults only. Dancers 17 and under compete in their age division.</p>
            </Question>

            <Question q="When is my music due?">
              <p>
                Before registration closes (about 2 weeks before competition day). Changes after that cost $20.
              </p>
            </Question>

            <Question q="Do I need a costume?">
              <p>No, but we encourage it. It makes the stage experience even more special.</p>
            </Question>

            <Question q="Should I bow at the end?">
              <p>It's up to you. If you want to bow, do it where you finish. No need to go back to the centre. Then exit the stage.</p>
            </Question>

            <Question q="Can I dance contemporary?">
              <p>Not this year. All entries must be classical ballet.</p>
            </Question>
          </Group>

          <Group id="day" title="Competition day">
            <Question q="When and where is the competition?">
              <p><strong>When:</strong> {eventWhen(season)}</p>
              <p><strong>Where:</strong> {venueText(season)}</p>
            </Question>

            <Question q="When will the schedule be ready?">
              <p>About 2 weeks before competition day. We email it to every dancer.</p>
            </Question>

            <Question q="Is there a first round and a final round?">
              <p>No. There is one round. Every dancer performs on the main stage.</p>
            </Question>

            <Question q="When are the results?">
              <p>
                Awards are given on the day, at the end of each session. Your written report from the judges comes
                later. You'll find it in your profile.
              </p>
            </Question>

            <Question q="Who are the judges?">
              <p>
                We will announce the {year} judges closer to the competition. You can meet our 2026 judges
                on the <InlineLink to="/2026">2026 Results &amp; Gallery</InlineLink> page.
              </p>
            </Question>

            <Question q="Can my family and friends come and watch?">
              <p>Yes! Everyone is welcome.</p>
              <ul>
                <li>A day pass is <strong>$20</strong> (cash at the door).</li>
                <li>Dancers get in free.</li>
                <li>Children under 12 get in free with one paying adult.</li>
                <li>Children under 5 usually can't come in.</li>
              </ul>
            </Question>
          </Group>

          <Group id="photos" title="Photos & video">
            <Question q="Do I get a video of my performance?">
              <p>Yes! Every dancer gets a professional video of their performance for free.</p>
            </Question>

            <Question q="Can I buy photos?">
              <p>
                Yes. You can buy photos on competition day. We will share the details 1 month before the
                competition.
              </p>
            </Question>
          </Group>

          <div style={{ textAlign: 'center', marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e0e0e0' }}>
            <p style={{ color: '#666', fontSize: '0.95rem', margin: 0, textAlign: 'center' }}>
              Can't find your answer? <InlineLink to="/contact">Contact us</InlineLink> and we'll help.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FAQ;
